import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const COLORS = [
  '#1B5E3B','#2E7D52','#3b82f6','#f97316','#22c55e',
  '#eab308','#ec4899','#14b8a6','#f43f5e','#06b6d4','#94a3b8'
];

const PRIORITY_NUMERIC = ['TotalValue', 'BasicValue', 'TaxValue', 'NetValue'];

function fmtINR(v) {
  const n = parseFloat(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtY(v) {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000)   return `${(v / 100000).toFixed(0)}L`;
  return v.toLocaleString('en-IN');
}

function detectColumns(columns, rows) {
  const result = { numeric: [], date: [], category: [], name: [] };
  columns.forEach(col => {
    const vals = rows.map(r => r[col]).filter(v => v != null && v !== '');
    if (!vals.length) return;
    if (vals.every(v => !isNaN(parseFloat(v)) && isFinite(parseFloat(v)))) {
      result.numeric.push(col);
      return;
    }
    if (vals.every(v => !isNaN(Date.parse(String(v))) && isNaN(Number(v)))) {
      result.date.push(col);
      return;
    }
    const unique = new Set(vals.map(String)).size;
    if (unique <= 8) result.category.push(col);
    else result.name.push(col);
  });
  return result;
}

const BarTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#1B5E3B', fontWeight: 600 }}>{fmtINR(payload[0].value)}</div>
    </div>
  );
};

const LineTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#1B5E3B', fontWeight: 600 }}>{fmtINR(payload[0].value)}</div>
    </div>
  );
};

const PieTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12, maxWidth: 200 }}>
      <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>{d.name}</div>
      <div style={{ color: '#1B5E3B' }}>{fmtINR(d.value)}</div>
      <div style={{ color: '#9b9b9b' }}>{d.payload.pct}% share</div>
    </div>
  );
};

export default function SmartDashboard({ rows, queryText, tableName, sql }) {
  if (!rows?.length) return null;

  const columns = Object.keys(rows[0]);
  const detected = detectColumns(columns, rows);

  const numericCols = [
    ...PRIORITY_NUMERIC.filter(c => detected.numeric.includes(c)),
    ...detected.numeric.filter(c => !PRIORITY_NUMERIC.includes(c))
  ];

  const primaryNum   = numericCols[0];
  const secondaryNum = numericCols[1];
  const nameCol      = detected.name[0] || detected.category[0];
  const dateCol      = detected.date[0];
  const categoryCol  = detected.category[0];
  const hasNumeric   = numericCols.length > 0;

  // ── KPI cards ───────────────────────────────────────────────────────────────
  const kpiCards = [];
  if (primaryNum) {
    const vals = rows.map(r => parseFloat(r[primaryNum]) || 0);
    const sum  = vals.reduce((a, b) => a + b, 0);
    kpiCards.push({ label: `Total ${primaryNum}`, value: fmtINR(sum) });
    kpiCards.push({ label: `Avg ${primaryNum}`,   value: fmtINR(sum / vals.length) });
  }
  if (secondaryNum) {
    const vals = rows.map(r => parseFloat(r[secondaryNum]) || 0);
    kpiCards.push({ label: `Total ${secondaryNum}`, value: fmtINR(vals.reduce((a, b) => a + b, 0)) });
  }
  kpiCards.push({ label: 'Records', value: rows.length.toLocaleString('en-IN') });
  const displayKpis = kpiCards.slice(0, 4);

  // ── Bar chart: top 10 by primaryNum grouped by nameCol ──────────────────────
  let barData = [];
  if (nameCol && primaryNum) {
    const grouped = {};
    rows.forEach(r => {
      const key = String(r[nameCol] ?? 'Unknown');
      grouped[key] = (grouped[key] || 0) + (parseFloat(r[primaryNum]) || 0);
    });
    barData = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({
        name: name.length > 20 ? name.slice(0, 18) + '…' : name,
        value
      }));
  }

  // ── Line chart: monthly trend from dateCol ──────────────────────────────────
  let lineData = [];
  if (dateCol && primaryNum) {
    const monthly = {};
    rows.forEach(r => {
      const d = new Date(r[dateCol]);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthly[key] = (monthly[key] || 0) + (parseFloat(r[primaryNum]) || 0);
    });
    lineData = Object.entries(monthly)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, value]) => ({ month, value }));
  }

  // ── Pie chart: category distribution by primaryNum ──────────────────────────
  let pieData = [];
  if (categoryCol && primaryNum) {
    const grouped = {};
    rows.forEach(r => {
      const key = String(r[categoryCol] ?? 'Unknown');
      grouped[key] = (grouped[key] || 0) + (parseFloat(r[primaryNum]) || 0);
    });
    const total = Object.values(grouped).reduce((a, b) => a + b, 0);
    pieData = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name,
        value,
        pct: total > 0 ? ((value / total) * 100).toFixed(1) : '0'
      }));
  }

  const showBar  = barData.length > 0;
  const showLine = lineData.length >= 2;
  const showPie  = pieData.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Header ── */}
      <div style={{ background: 'linear-gradient(135deg, #E8F6EE, #f0fdf4)', border: '1px solid #C8EDD6', borderRadius: 14, padding: '16px 20px', animation: 'sectionReveal 0.4s ease 0s both', boxShadow: '0 2px 10px rgba(27,94,59,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#1B5E3B,#2E7D52)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(27,94,59,0.3)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.3px', margin: 0 }}>Smart Dashboard</h2>
          {tableName && (
            <span style={{ background: '#E0F5EE', color: '#1B5E3B', fontSize: 11, padding: '2px 9px', borderRadius: 12, fontWeight: 600 }}>
              {tableName}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {queryText && (
            <span style={{ background: '#fff', border: '1px solid #C8EDD6', borderRadius: 20, padding: '3px 12px', fontSize: 12, color: '#4b4b4b', maxWidth: 440, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              "{queryText}"
            </span>
          )}
          <span style={{ fontSize: 12, color: '#9b9b9b' }}>{rows.length} rows · instant render</span>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'flex', gap: 10, animation: 'sectionReveal 0.4s ease 0.08s both' }}>
        {displayKpis.map((kpi, i) => (
          <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid #e8e6e0', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#9b9b9b', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{kpi.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1B5E3B', lineHeight: 1.2 }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* ── No-charts fallback ── */}
      {!hasNumeric && (
        <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 12, padding: '32px 16px', textAlign: 'center', color: '#9b9b9b', fontSize: 13 }}>
          No numeric columns detected — charts not available for this result
        </div>
      )}

      {/* ── Bar Chart ── */}
      {showBar && (
        <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', animation: 'sectionReveal 0.4s ease 0.16s both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: '#4b4b4b', marginBottom: 14 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            Top {barData.length} by {primaryNum} · {nameCol}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9b9b9b' }} tickLine={false} angle={-35} textAnchor="end" interval={0} />
              <YAxis tickFormatter={fmtY} tick={{ fontSize: 10, fill: '#9b9b9b' }} tickLine={false} axisLine={false} width={52} />
              <Tooltip content={<BarTip />} />
              <Bar dataKey="value" fill="#1B5E3B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Line Chart ── */}
      {showLine && (
        <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', animation: 'sectionReveal 0.4s ease 0.24s both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: '#4b4b4b', marginBottom: 14 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            Monthly Trend · {primaryNum}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lineData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9b9b9b' }} tickLine={false} />
              <YAxis tickFormatter={fmtY} tick={{ fontSize: 10, fill: '#9b9b9b' }} tickLine={false} axisLine={false} width={52} />
              <Tooltip content={<LineTip />} />
              <Line type="monotone" dataKey="value" stroke="#1B5E3B" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Pie / Donut Chart ── */}
      {showPie && (
        <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', animation: 'sectionReveal 0.4s ease 0.32s both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: '#4b4b4b', marginBottom: 14 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/>
              <path d="M22 12A10 10 0 0 0 12 2v10z"/>
            </svg>
            {categoryCol} Distribution
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={82} dataKey="value" paddingAngle={2}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, overflow: 'hidden' }}>
              {pieData.slice(0, 8).map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: COLORS[i % COLORS.length] }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#4b4b4b' }}>{d.name}</span>
                  <span style={{ color: '#1B5E3B', fontWeight: 600, flexShrink: 0 }}>{d.pct}%</span>
                </div>
              ))}
              {pieData.length > 8 && (
                <div style={{ fontSize: 10, color: '#9b9b9b' }}>+{pieData.length - 8} more</div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
